import React, {useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {momentsService} from '../lib/moments-service';

function Media({blob, type = 'image', preview = false, ...props}) {
  const [url, setUrl] = useState('');

  useEffect(() => {
    if (!blob) {
      setUrl('');
      return undefined;
    }

    const next = URL.createObjectURL(blob);
    setUrl(next);

    return () => URL.revokeObjectURL(next);
  }, [blob]);

  if (!url) {
    return null;
  }

  if (type === 'video') {
    return (
      <video
        src={url}
        playsInline
        muted={preview}
        controls={!preview}
        {...props}
      />
    );
  }

  return <img src={url} {...props} />;
}

function CameraIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M9.2 4 7.8 6H5a3 3 0 0 0-3 3v8a3 3 0 0 0 3 3h14a3 3 0 0 0 3-3V9a3 3 0 0 0-3-3h-2.8l-1.4-2H9.2Zm2.8 5a4.5 4.5 0 1 1 0 9 4.5 4.5 0 0 1 0-9Zm0 2a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5Z"
      />
    </svg>
  );
}

function GalleryIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Zm0 2v10.6l3.8-3.8 2.7 2.7 2.2-2.2L19 17.6V5H5Zm11 2.2a1.8 1.8 0 1 0 0 3.6 1.8 1.8 0 0 0 0-3.6Z"
      />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M11 5h2v6h6v2h-6v6h-2v-6H5v-2h6V5Z"
      />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="m6.4 5 5.6 5.6L17.6 5 19 6.4 13.4 12l5.6 5.6-1.4 1.4-5.6-5.6L6.4 19 5 17.6l5.6-5.6L5 6.4 6.4 5Z"
      />
    </svg>
  );
}

function Chevron({direction}) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d={
          direction === 'left'
            ? 'M15.4 5 8.4 12l7 7L14 20.4 5.6 12 14 3.6 15.4 5Z'
            : 'M8.6 5 10 3.6l8.4 8.4-8.4 8.4L8.6 19l7-7-7-7Z'
        }
      />
    </svg>
  );
}

function formatAge(timestamp) {
  const diff = Math.max(0, Date.now() - timestamp);
  const minutes = Math.floor(diff / 60000);

  if (minutes < 1) {
    return 'сейчас';
  }

  if (minutes < 60) {
    return `${minutes} мин.`;
  }

  const hours = Math.floor(minutes / 60);

  if (hours < 24) {
    return `${hours} ч.`;
  }

  return 'вчера';
}

export default function Moments({ownerId, author, createRequest}) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('home');
  const [index, setIndex] = useState(null);
  const [file, setFile] = useState(null);
  const [caption, setCaption] = useState('');
  const [textMoment, setTextMoment] = useState('');
  const [textBackground, setTextBackground] = useState('sunset');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(0);

  const dialog = useRef(null);
  const cameraInput = useRef(null);
  const galleryInput = useRef(null);

  const refresh = () =>
    momentsService
      .list(ownerId)
      .then(setRows)
      .catch(() => setError('Не удалось открыть хранилище моментов.'));

  useEffect(() => {
    refresh();
  }, [ownerId]);

  const resetCreate = () => {
    setFile(null);
    setCaption('');
    setTextMoment('');
    setTextBackground('sunset');
    setError('');
  };

  const close = () => {
    setOpen(false);
    setMode('home');
    setIndex(null);
    setPaused(false);
    setProgress(0);
    resetCreate();
  };

  const openMoments = () => {
    refresh();
    setOpen(true);
    setMode('home');
    setIndex(null);
    setError('');
  };

  const openCreate = () => {
    setOpen(true);
    setMode('create');
    setIndex(null);
    setError('');
  };

  useEffect(() => { if (createRequest) openCreate(); }, [createRequest]);

  const openViewer = position => {
    if (!rows[position]) {
      return;
    }

    setOpen(true);
    setMode('viewer');
    setIndex(position);
    setPaused(false);
    setProgress(0);
  };

  const next = () => {
    setProgress(0);

    setIndex(currentIndex => {
      if (currentIndex === null) {
        return null;
      }

      if (currentIndex + 1 < rows.length) {
        return currentIndex + 1;
      }

      setMode('home');
      return null;
    });
  };

  const previous = () => {
    setProgress(0);

    setIndex(currentIndex => {
      if (currentIndex === null) {
        return null;
      }

      return Math.max(0, currentIndex - 1);
    });
  };

  const current = index === null ? null : rows[index];

  useEffect(() => {
    if (!current) {
      return;
    }

    momentsService
      .markViewed(current)
      .then(refresh)
      .catch(() => setError('Не удалось сохранить просмотр.'));

    setProgress(0);
  }, [current?.id]);

  useEffect(() => {
    if (!current || paused || mode !== 'viewer') {
      return undefined;
    }

    const timer = setInterval(() => {
      setProgress(value => Math.min(100, value + 2));
    }, 100);

    return () => clearInterval(timer);
  }, [current?.id, paused, mode]);

  useEffect(() => {
    if (progress >= 100 && mode === 'viewer') {
      next();
    }
  }, [progress]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const before = document.activeElement;
    const background = document.getElementById('mountPoint');

    if (background) {
      background.inert = true;
    }

    dialog.current?.focus();

    return () => {
      if (background) {
        background.inert = false;
      }

      before?.focus();
    };
  }, [open]);

  const keys = event => {
    if (event.key === 'Escape') {
      close();
      return;
    }

    if (mode === 'viewer' && current && event.key === 'ArrowRight') {
      next();
    }

    if (mode === 'viewer' && current && event.key === 'ArrowLeft') {
      previous();
    }

    if (event.key === 'Tab' && dialog.current) {
      const nodes = [
        ...dialog.current.querySelectorAll(
          'button:not(:disabled), input:not(:disabled), textarea:not(:disabled)'
        )
      ];

      const first = nodes[0];
      const last = nodes[nodes.length - 1];

      if (
        event.shiftKey &&
        (document.activeElement === first ||
          document.activeElement === dialog.current)
      ) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    }
  };

  const chooseFile = event => {
    const selected = event.target.files?.[0];

    if (!selected) {
      return;
    }

    setFile(selected);
    setError('');
    setMode('create');
    event.target.value = '';
  };

  const publish = async event => {
    event.preventDefault();

    if (!file) {
      setError('Выберите фото или видео.');
      return;
    }

    setBusy(true);
    setError('');

    try {
      await momentsService.create(
        ownerId,
        author || 'Вы',
        file,
        caption
      );

      await refresh();

      resetCreate();
      setMode('home');
    } catch (err) {
      setError(err.message || 'Не удалось сохранить момент.');
    } finally {
      setBusy(false);
    }
  };

  const publishText = async event => {
    event.preventDefault();

    setBusy(true);
    setError('');

    try {
      await momentsService.createText(
        ownerId,
        author || 'Вы',
        textMoment,
        textBackground
      );

      await refresh();

      resetCreate();
      setMode('home');
    } catch (err) {
      setError(err.message || 'Не удалось сохранить текстовый момент.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async row => {
    if (!row) {
      return;
    }

    try {
      await momentsService.remove(row.id);
      await refresh();

      setIndex(null);
      setMode('home');
    } catch {
      setError('Не удалось удалить момент.');
    }
  };

  const hasUnseen = rows.some(row => !row.viewed);
  const latest = rows[0];

  return (
    <>
      <section className="fox-moments-strip" aria-label="Моменты">
        <button
          className="fox-moment-person fox-moment-person-own"
          type="button"
          onClick={openCreate}
          aria-label="Добавить момент"
        >
          <span className="fox-moment-avatar fox-moment-avatar-own">
            <span className="fox-moment-avatar-letter">
              {(author || 'Вы').trim().charAt(0).toUpperCase() || 'В'}
            </span>
            <span className="fox-moment-add">
              <PlusIcon />
            </span>
          </span>

          <span className="fox-moment-person-name">Ваш момент</span>
        </button>

        {rows.length > 0 && (
          <button
            className="fox-moment-person"
            type="button"
            onClick={() => openViewer(0)}
          >
            <span
              className={
                'fox-moment-avatar ' +
                (hasUnseen ? 'is-unseen' : 'is-seen')
              }
            >
              <span className="fox-moment-avatar-inner">
                {latest?.media ? (
                  <Media
                    blob={latest.media}
                    type={latest.mediaType}
                    preview
                  />
                ) : (
                  <span className="fox-moment-avatar-letter">
                    {(author || 'Вы').trim().charAt(0).toUpperCase() || 'В'}
                  </span>
                )}
              </span>
            </span>

            <span className="fox-moment-person-name">
              {author || 'Вы'}
            </span>
          </button>
        )}

        <button
          className="fox-moments-all"
          type="button"
          onClick={openMoments}
        >
          <span className="fox-moments-all-icon">◉</span>
          <span>
            <strong>Моменты</strong>
            <small>
              {rows.length
                ? `${rows.length} за 24 часа`
                : 'Посмотреть'}
            </small>
          </span>
        </button>
      </section>

      {open &&
        createPortal(
          <div
            className={
              'fox-moments-overlay fox-moments-mode-' + mode
            }
            role="dialog"
            aria-modal="true"
            aria-label="Моменты"
            tabIndex={-1}
            ref={dialog}
            onKeyDown={keys}
          >
            <div className="fox-moments-shell">
              {mode !== 'viewer' && (
                <header className="fox-moments-header">
                  <div>
                    <h2>{mode === 'create' || mode === 'text' ? 'Новый момент' : 'Моменты'}</h2>
                    {mode === 'home' && (
                      <p>Фото, видео и мысли на 24 часа</p>
                    )}
                  </div>

                  <button
                    className="fox-moments-close"
                    type="button"
                    onClick={close}
                    aria-label="Закрыть"
                  >
                    <CloseIcon />
                  </button>
                </header>
              )}

              {mode === 'home' && (
                <main className="fox-moments-home">
                  <section className="fox-moments-create-card">
                    <div className="fox-moments-create-copy">
                      <span className="fox-moments-create-kicker">
                        Новый момент
                      </span>
                      <strong>
                        Покажите, чем вы хотите поделиться
                      </strong>
                      <span>
                        Фото или видео будет доступно 24 часа
                      </span>
                    </div>

                    <div className="fox-moments-actions">
                      <button
                        type="button"
                        onClick={() => cameraInput.current?.click()}
                      >
                        <span className="fox-moment-action-icon">
                          <CameraIcon />
                        </span>
                        <strong>Камера</strong>
                        <small>Снять сейчас</small>
                      </button>

                      <button
                        type="button"
                        onClick={() => galleryInput.current?.click()}
                      >
                        <span className="fox-moment-action-icon">
                          <GalleryIcon />
                        </span>
                        <strong>Фото и видео</strong>
                        <small>Выбрать с устройства</small>
                      </button>
                    </div>
                  </section>

                  <section className="fox-moments-library">
                    <div className="fox-moments-library-title">
                      <div>
                        <h3>Ваши моменты</h3>
                        <span>
                          {rows.length
                            ? `${rows.length} активно`
                            : 'Пока ничего нет'}
                        </span>
                      </div>

                      <button
                        type="button"
                        className="fox-moment-new-small"
                        onClick={openCreate}
                      >
                        <PlusIcon />
                        Добавить
                      </button>
                    </div>

                    {rows.length === 0 ? (
                      <div className="fox-moments-empty">
                        <span className="fox-moments-empty-ring">
                          <CameraIcon />
                        </span>

                        <strong>Создайте первый момент</strong>

                        <p>
                          Фотография или видео появится здесь после
                          публикации.
                        </p>
                      </div>
                    ) : (
                      <div className="fox-moments-grid">
                        {rows.map((row, position) => (
                          <article key={row.id}>
                            <button
                              type="button"
                              className={
                                'fox-moment-card ' +
                                (!row.viewed ? 'is-unseen' : '')
                              }
                              onClick={() => openViewer(position)}
                            >
                              <span
                                className={
                                  'fox-moment-card-media ' +
                                  (row.mediaType === 'text'
                                    ? 'fox-text-bg-' + (row.background || 'sunset')
                                    : '')
                                }
                              >
                                {row.mediaType === 'text' ? (
                                  <span className="fox-moment-card-text">
                                    {row.text}
                                  </span>
                                ) : (
                                  <Media
                                    blob={row.media}
                                    type={row.mediaType}
                                    preview
                                  />
                                )}

                                {row.mediaType === 'video' && (
                                  <span className="fox-moment-video-badge">
                                    ▶
                                  </span>
                                )}
                              </span>

                              <span className="fox-moment-card-meta">
                                <strong>
                                  {row.mediaType === 'text'
                                    ? row.text
                                    : row.caption || 'Ваш момент'}
                                </strong>
                                <small>
                                  {formatAge(row.createdAt)}
                                </small>
                              </span>
                            </button>
                          </article>
                        ))}
                      </div>
                    )}
                  </section>
                </main>
              )}

              {mode === 'create' && (
                <main className="fox-moment-editor">
                  <button
                    type="button"
                    className="fox-moment-editor-floating-back"
                    onClick={() => {
                      resetCreate();
                      setMode('home');
                    }}
                    aria-label="Назад"
                  >
                    ‹
                  </button>

                  {!file ? (
                    <div className="fox-moment-picker fox-moment-picker-compact">
                      <div className="fox-moment-picker-hero">
                        <span>
                          <CameraIcon />
                        </span>

                        <h3>Создать момент</h3>

                        <p>
                          Снимите что-нибудь сейчас, выберите медиа или
                          поделитесь мыслью.
                        </p>
                      </div>

                      <div className="fox-moment-picker-actions fox-moment-picker-actions-three">
                        <button
                          type="button"
                          onClick={() => cameraInput.current?.click()}
                        >
                          <span>
                            <CameraIcon />
                          </span>
                          <strong>Камера</strong>
                        </button>

                        <button
                          type="button"
                          onClick={() => galleryInput.current?.click()}
                        >
                          <span>
                            <GalleryIcon />
                          </span>
                          <strong>Фото и видео</strong>
                        </button>

                        <button
                          type="button"
                          onClick={() => {
                            resetCreate();
                            setMode('text');
                          }}
                        >
                          <span className="fox-moment-text-icon">Aa</span>
                          <strong>Текст</strong>
                        </button>
                      </div>
                    </div>
                  ) : (
                    <form
                      className="fox-moment-compose"
                      onSubmit={publish}
                    >
                      <div className="fox-moment-compose-media">
                        <Media
                          blob={file}
                          type={
                            file.type.startsWith('video/')
                              ? 'video'
                              : 'image'
                          }
                          preview={file.type.startsWith('video/')}
                        />

                        <button
                          type="button"
                          className="fox-moment-change-media"
                          onClick={() => galleryInput.current?.click()}
                        >
                          Изменить
                        </button>
                      </div>

                      <div className="fox-moment-compose-bottom">
                        <textarea
                          value={caption}
                          maxLength={500}
                          onChange={event =>
                            setCaption(event.target.value)
                          }
                          placeholder="Добавить подпись…"
                        />

                        <div className="fox-moment-compose-footer">
                          <span>{caption.length}/500</span>

                          <button
                            className="fox-moment-publish"
                            type="submit"
                            disabled={busy}
                          >
                            {busy ? 'Публикуем…' : 'Опубликовать'}
                          </button>
                        </div>
                      </div>
                    </form>
                  )}
                </main>
              )}

              {mode === 'text' && (
                <main className="fox-text-moment-editor">
                  <button
                    type="button"
                    className="fox-moment-editor-floating-back"
                    onClick={() => {
                      resetCreate();
                      setMode('create');
                    }}
                    aria-label="Назад"
                  >
                    ‹
                  </button>

                  <form
                    className={'fox-text-moment-canvas fox-text-bg-' + textBackground}
                    onSubmit={publishText}
                  >
                    <textarea
                      autoFocus
                      value={textMoment}
                      maxLength={500}
                      onChange={event => setTextMoment(event.target.value)}
                      placeholder="Напишите что-нибудь…"
                      aria-label="Текст момента"
                    />

                    <div className="fox-text-moment-bottom">
                      <div
                        className="fox-text-backgrounds"
                        aria-label="Выберите фон"
                      >
                        {['sunset', 'night', 'ocean', 'berry', 'forest', 'ember'].map(background => (
                          <button
                            key={background}
                            type="button"
                            className={
                              'fox-text-background fox-text-background-' +
                              background +
                              (textBackground === background ? ' is-active' : '')
                            }
                            onClick={() => setTextBackground(background)}
                            aria-label={'Фон ' + background}
                          />
                        ))}
                      </div>

                      <div className="fox-text-moment-publish-row">
                        <span>{textMoment.length}/500</span>

                        <button
                          className="fox-moment-publish"
                          type="submit"
                          disabled={busy || !textMoment.trim()}
                        >
                          {busy ? 'Публикуем…' : 'Опубликовать'}
                        </button>
                      </div>
                    </div>
                  </form>
                </main>
              )}

              {mode === 'viewer' && current && (
                <main
                  className="fox-moment-viewer"
                  onMouseEnter={() => setPaused(true)}
                  onMouseLeave={() => setPaused(false)}
                >
                  <div className="fox-moment-progress">
                    {rows.map((row, position) => (
                      <span key={row.id}>
                        <b
                          style={{
                            width:
                              position < index
                                ? '100%'
                                : position === index
                                  ? `${progress}%`
                                  : '0%'
                          }}
                        />
                      </span>
                    ))}
                  </div>

                  <div className="fox-moment-viewer-head">
                    <div className="fox-moment-viewer-author">
                      <span className="fox-moment-viewer-avatar">
                        {(current.author || 'Вы')
                          .trim()
                          .charAt(0)
                          .toUpperCase() || 'В'}
                      </span>

                      <span>
                        <strong>{current.author || 'Вы'}</strong>
                        <small>
                          {formatAge(current.createdAt)}
                        </small>
                      </span>
                    </div>

                    <div className="fox-moment-viewer-controls">
                      <button
                        type="button"
                        onClick={() => remove(current)}
                        aria-label="Удалить момент"
                        title="Удалить"
                      >
                        ⋯
                      </button>

                      <button
                        type="button"
                        onClick={close}
                        aria-label="Закрыть"
                      >
                        <CloseIcon />
                      </button>
                    </div>
                  </div>

                  <div
                    className={
                      'fox-moment-stage ' +
                      (current.mediaType === 'text'
                        ? 'fox-text-bg-' + (current.background || 'sunset')
                        : '')
                    }
                  >
                    {current.mediaType === 'text' ? (
                      <div className="fox-moment-viewer-text">
                        {current.text}
                      </div>
                    ) : (
                      <Media
                        blob={current.media}
                        type={current.mediaType}
                        autoPlay={current.mediaType === 'video'}
                      />
                    )}

                    {index > 0 && (
                      <button
                        className="fox-moment-nav fox-moment-nav-prev"
                        type="button"
                        onClick={previous}
                        aria-label="Предыдущий момент"
                      >
                        <Chevron direction="left" />
                      </button>
                    )}

                    {index < rows.length - 1 && (
                      <button
                        className="fox-moment-nav fox-moment-nav-next"
                        type="button"
                        onClick={next}
                        aria-label="Следующий момент"
                      >
                        <Chevron direction="right" />
                      </button>
                    )}

                    <button
                      type="button"
                      className="fox-moment-tap fox-moment-tap-left"
                      onClick={previous}
                      aria-label="Предыдущий момент"
                    />

                    <button
                      type="button"
                      className="fox-moment-tap fox-moment-tap-right"
                      onClick={next}
                      aria-label="Следующий момент"
                    />
                  </div>

                  {current.caption && (
                    <p className="fox-moment-caption">
                      {current.caption}
                    </p>
                  )}
                </main>
              )}

              {error && (
                <div className="fox-moment-error">
                  {error}
                </div>
              )}

              <input
                ref={cameraInput}
                className="fox-moment-native-input"
                type="file"
                accept="image/*,video/*"
                capture="environment"
                onChange={chooseFile}
              />

              <input
                ref={galleryInput}
                className="fox-moment-native-input"
                type="file"
                accept="image/*,video/*"
                onChange={chooseFile}
              />
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
